import React from 'react';
import { Box, Chip, CircularProgress, Tooltip, Typography, useTheme } from '@mui/material';
import { getDocTypeColor, getCitationSectionTitle } from '../../../utils/utils';

/**
 * Compact document chip for timeline display
 * Shows doc_type tag (colored) and truncated title
 */
const DocumentChip = ({ document, onClick, compact = true, maxTitleLength = 35, loading = false }) => {
  const theme = useTheme();
  const { metadata } = document || {};

  if (!metadata) return null;

  const docTypeColor = getDocTypeColor(metadata?.doc_type);
  const title = metadata?.doc_type === 'case-law'
    ? (metadata?.case_number || metadata?.subject_matter || 'Case')
    : (metadata?.title || 'Document');

  // Truncate title for compact display
  const displayTitle = compact && title.length > maxTitleLength
    ? `${title.substring(0, maxTitleLength)}...`
    : title;

  // Get abbreviated doc type (first 3 characters)
  const docTypeAbbrev = metadata?.doc_type
    ? metadata.doc_type.substring(0, 3).toUpperCase()
    : 'DOC';

  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 280 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
          {metadata?.section_title && (
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
              {getCitationSectionTitle(metadata.section_title)}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
      enterDelay={300}
    >
      <Chip
        size="small"
        onClick={onClick}
        sx={{
          height: 24,
          maxWidth: compact ? 160 : 'none',
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: `${docTypeColor}10`,
          border: `1px solid ${docTypeColor}25`,
          borderRadius: '6px',
          transition: 'all 0.15s ease',
          '&:hover': onClick ? {
            backgroundColor: `${docTypeColor}18`,
            borderColor: `${docTypeColor}40`,
            transform: 'translateY(-1px)',
          } : {},
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0,
          },
        }}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
            {/* Doc Type Tag or Loading Spinner */}
            {loading ? (
              <CircularProgress
                size={12}
                thickness={5}
                sx={{ color: docTypeColor, flexShrink: 0 }}
              />
            ) : (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: docTypeColor,
                  color: '#ffffff',
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  px: 0.5,
                  py: 0.1,
                  borderRadius: '3px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.2px',
                  minWidth: 'fit-content',
                  flexShrink: 0,
                  lineHeight: 1.4,
                }}
              >
                {docTypeAbbrev}
              </Box>
            )}

            {/* Title */}
            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {displayTitle}
            </Typography>
          </Box>
        }
      />
    </Tooltip>
  );
};

export default DocumentChip;
